import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DataGridPro, GridActionsCellItem, } from "@mui/x-data-grid-pro";
import { formatTimestamp, usePagedListing, useSafeSistaAsync, } from "@sista/library-stasi";
import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertsContext, DistaConfirmDialog, DistaTooltip, DistaTooltipVariant, } from "@sista/dista-ui";
import { Delete, Edit, RestoreFromTrash, Token } from "@mui/icons-material";
import { Box, FormControlLabel, Stack, Switch, Typography, } from "@mui/material";
import { useSpravaTextuClient } from "../../util/useSpravaTextuClient";
import { useIsAdmin } from "../../util/useIsAdmin";
import { EditNamespaceDialog } from "../dialogs/EditNamespaceDialog";
const pageSize = 100;
const iconSize = { width: "20px", height: "20px" };
const tooltipMaxWidth = {
    "& .MuiTooltip-tooltip": {
        maxWidth: "60rem",
    },
};
export const NamespaceTable = ({ canEdit, onNamespaceSelected, }) => {
    const { t } = useTranslation("sprava-textu");
    const spratex = useSpravaTextuClient();
    const { showInfo, showSuccess } = useContext(AlertsContext);
    const isAdmin = useIsAdmin();
    const [editingNamespace, setEditingNamespace] = useState(null);
    const [deletingNamespace, setDeletingNamespace] = useState(null);
    const [undeletingNamespace, setUndeletingNamespace] = useState(null);
    const [showDeleted, setShowDeleted] = useState(false);
    const [getAllNamespacesCall, getAllNameSpacesState] = useSafeSistaAsync(spratex.findNamespaces);
    const [deleteNamespaceCall] = useSafeSistaAsync(spratex.deleteNamespace);
    const [undeleteNamespaceCall] = useSafeSistaAsync(spratex.undeleteNamespace);
    const loader = useCallback(async (page) => {
        const result = await getAllNamespacesCall({
            page: page,
            pageSize: pageSize,
        });
        if (result == null) {
            return [];
        }
        return result;
    }, [getAllNamespacesCall]);
    const { currentPage, error, pending, refresh, rows } = usePagedListing({
        loader,
        maxRows: 1000,
    });
    // Smazané prostory server vrací pouze adminovi, ale ani ten je defaultně vidět nechce.
    const visibleRows = useMemo(() => {
        if (showDeleted)
            return rows;
        return rows.filter((row) => row.dateDeleted == null);
    }, [rows, showDeleted]);
    const deleteNamespaceHandler = useCallback(async (namespace) => {
        const result = await deleteNamespaceCall({
            namespace: namespace.namespace,
        });
        if (typeof result === "object" && result) {
            showSuccess(t("sprava-textu.namespaceTable.namespaceDeleted", "Jmenný prostor smazán"));
            refresh();
        }
    }, [deleteNamespaceCall, refresh, showSuccess, t]);
    const undeleteNamespaceHandler = useCallback(async (namespace) => {
        const result = await undeleteNamespaceCall({
            namespace: namespace.namespace,
        });
        if (typeof result === "object" && result) {
            showSuccess(t("sprava-textu.namespaceTable.namespaceUndeleted", "Jmenný prostor obnoven"));
            refresh();
        }
    }, [undeleteNamespaceCall, refresh, showSuccess, t]);
    const columns = useMemo(() => {
        return [
            {
                field: "namespace",
                headerName: t("sprava-textu.namespaceTable.namespace", "Jmenný prostor"),
                sortable: true,
                flex: 2,
            },
            {
                field: "importToken",
                type: "actions",
                headerName: t("sprava-textu.namespaceTable.importToken", "Import token"),
                width: 100,
                // Se smazaným jmenným prostorem nelze pracovat, import mu vrací 404.
                getActions: (params) => params.row.dateDeleted != null
                    ? []
                    : [
                        _jsx(DistaTooltip, { title: t("sprava-textu.namespaceTable.importTokenTooltip", 'Kliknutím zkopírujete token "{{token}}" do schránky.', { token: params.row.importToken }), variant: DistaTooltipVariant.info, sx: tooltipMaxWidth, children: _jsx(GridActionsCellItem, { icon: _jsx(Token, {}), label: t("sprava-textu.textsTable.getToken", "Získat token"), disabled: false, onClick: () => {
                                    showInfo(t("sprava-textu.namespaceTable.importTokenInfo", 'Token "{{token}}" byl zkopírován do schránky.', { token: params.row.importToken }));
                                    navigator.clipboard.writeText(params.row.importToken);
                                } }, "token") }, "token-tooltip"),
                    ],
            },
            {
                field: "format",
                headerName: t("sprava-textu.namespaceTable.format", "Formát"),
                sortable: true,
                flex: 2,
                /* valueGetter: (params: GridRenderCellParams) => {
                      // formated date:
                      if (params.row.dateCreated == null) return "";
                      return formatTimestamp(params.row.dateCreated);
                    }, */
            },
            {
                field: "totalTextsCount",
                headerName: t("sprava-textu.namespaceTable.totalTextsCount", "Celkem textů"),
                sortable: true,
                flex: 1,
            },
            {
                field: "textsUnApprovedCount",
                headerName: t("sprava-textu.namespaceTable.textsUnApprovedCount", "Neschválené texty"),
                sortable: true,
                flex: 1,
            },
            {
                field: "lastChange",
                headerName: t("sprava-textu.namespaceTable.lastChange", "Poslední změna"),
                sortable: true,
                flex: 2,
                valueGetter: (params) => {
                    // formated date:
                    if (params.row.lastChange == null)
                        return "";
                    return formatTimestamp(params.row.lastChange);
                },
            },
            {
                field: "dateCreated",
                headerName: t("sprava-textu.namespaceTable.dateCreated", "Datum vytvoření"),
                sortable: true,
                flex: 2,
                valueGetter: (params) => {
                    // formated date:
                    if (params.row.dateCreated == null)
                        return "";
                    return formatTimestamp(params.row.dateCreated);
                },
            },
            // Bez zobrazených smazaných prostorů by sloupec byl vždy prázdný.
            isAdmin && showDeleted
                ? {
                    field: "dateDeleted",
                    headerName: t("sprava-textu.namespaceTable.dateDeleted", "Smazáno"),
                    sortable: true,
                    flex: 2,
                    valueGetter: (params) => {
                        if (params.row.dateDeleted == null)
                            return "";
                        return formatTimestamp(params.row.dateDeleted);
                    },
                }
                : null,
            canEdit || isAdmin
                ? {
                    field: "actions",
                    type: "actions",
                    headerName: t("sprava-textu.namespaceTable.actions", "Akce"),
                    width: 110,
                    getActions: (params) => {
                        // Nad smazaným prostorem lze jen jedno - obnovit ho.
                        if (params.row.dateDeleted != null) {
                            return [
                                _jsx(GridActionsCellItem, { icon: _jsx(RestoreFromTrash, { sx: iconSize }), label: t("sprava-textu.namespaceTable.undeleteNamespace", "Obnovit"), onClick: () => setUndeletingNamespace(params.row) }, "undelete"),
                            ];
                        }
                        return [
                            canEdit ? (_jsx(GridActionsCellItem, { icon: _jsx(Edit, { sx: iconSize }), label: t("sprava-textu.namespaceTable.editNamespace", "Upravit"), onClick: () => setEditingNamespace(params.row) }, "edit")) : null,
                            isAdmin ? (_jsx(GridActionsCellItem, { icon: _jsx(Delete, { sx: iconSize, color: "error" }), label: t("sprava-textu.namespaceTable.deleteNamespace", "Smazat"), onClick: () => setDeletingNamespace(params.row) }, "delete")) : null,
                        ].filter(Boolean);
                    },
                }
                : null,
        ].filter(Boolean);
    }, [
        t,
        showInfo,
        setEditingNamespace,
        setDeletingNamespace,
        setUndeletingNamespace,
        canEdit,
        isAdmin,
        showDeleted,
    ]);
    return (_jsxs(_Fragment, { children: [editingNamespace != null && (_jsx(EditNamespaceDialog, { namespace: editingNamespace, onSuccess: refresh, onClose: () => setEditingNamespace(null) })), deletingNamespace != null && (_jsx(DistaConfirmDialog, { open: true, onClose: () => setDeletingNamespace(null), title: t("sprava-textu.namespaceTable.deleteConfirmDialog.title", "Smazat jmenný prostor"), okAction: {
                    type: "button",
                    children: t("sprava-textu.namespaceTable.deleteConfirmDialog.ok", "Smazat"),
                    onClick: () => {
                        deleteNamespaceHandler(deletingNamespace);
                        setDeletingNamespace(null);
                    },
                }, cancelAction: {
                    type: "button",
                    children: t("sprava-textu.namespaceTable.deleteConfirmDialog.cancel", "Zrušit"),
                    onClick: () => setDeletingNamespace(null),
                }, children: _jsxs(Stack, { direction: "column", spacing: 1, children: [_jsx(Typography, { variant: "body1", sx: { fontWeight: "bold" }, children: deletingNamespace.namespace }), _jsx(Typography, { children: t("sprava-textu.namespaceTable.deleteConfirmDialog.text", "Texty se nezahazují a prostor lze později obnovit. Do té doby ale jeho texty nikdo neuvidí, nepůjde do něj importovat ani z něj stahovat a prostor stejného jména nepůjde znovu založit.") })] }) })), undeletingNamespace != null && (_jsx(DistaConfirmDialog, { open: true, onClose: () => setUndeletingNamespace(null), title: t("sprava-textu.namespaceTable.undeleteConfirmDialog.title", "Obnovit jmenný prostor"), okAction: {
                    type: "button",
                    children: t("sprava-textu.namespaceTable.undeleteConfirmDialog.ok", "Obnovit"),
                    onClick: () => {
                        undeleteNamespaceHandler(undeletingNamespace);
                        setUndeletingNamespace(null);
                    },
                }, cancelAction: {
                    type: "button",
                    children: t("sprava-textu.namespaceTable.undeleteConfirmDialog.cancel", "Zrušit"),
                    onClick: () => setUndeletingNamespace(null),
                }, children: _jsxs(Stack, { direction: "column", spacing: 1, children: [_jsx(Typography, { variant: "body1", sx: { fontWeight: "bold" }, children: undeletingNamespace.namespace }), _jsx(Typography, { children: t("sprava-textu.namespaceTable.undeleteConfirmDialog.text", "Jmenný prostor i všechny jeho texty se vrátí do původního stavu.") })] }) })), _jsxs(Box, { sx: { height: "100%", display: "flex", flexDirection: "column" }, children: [isAdmin && (_jsx(Stack, { direction: "row", sx: { justifyContent: "flex-end" }, children: _jsx(FormControlLabel, { control: _jsx(Switch, { size: "small", checked: showDeleted, onChange: (e) => setShowDeleted(e.target.checked) }), label: _jsx(Typography, { variant: "body2", children: t("sprava-textu.namespaceTable.showDeleted", "Zobrazit smazané") }) }) })), _jsx(Box, { sx: { flexGrow: 1, minHeight: 0 }, children: _jsx(DataGridPro, { columns: columns, rows: visibleRows, getRowId: (row) => row.namespace + "|" + row.format, getRowClassName: (params) => params.row.dateDeleted != null ? "row-disabled" : "", pagination: true, pageSizeOptions: [pageSize], loading: currentPage < 1 && pending }) })] })] }));
};
